import React, { useState, useEffect, useRef } from 'react';
import './MessagingPage.css';
import {
  Search,
  MoreVertical,
  Filter,
  Send,
  Paperclip,
  Smile,
  Image,
  FileText,
  Video,
  Mic,
  Phone,
  Video as VideoIcon,
  Info,
  X,
  Check,
  CheckCheck,
  Clock,
  User,
  Users,
  Star,
  Pin,
  Archive,
  Trash2,
  Bell,
  BellOff,
  Link,
  MessageCircle,
  Calendar,
  MapPin,
  FileUp
} from 'lucide-react';

const MessagingPage = () => {
  const [activeChat, setActiveChat] = useState(1);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeChat]);

  const conversations = [
    {
      id: 1,
      name: 'Sarah Chen',
      role: 'Senior Product Designer at Google',
      avatar: 'SC',
      unread: 3,
      lastMessage: 'Thanks for sharing the design files!',
      timestamp: '10:30 AM',
      online: true,
      pinned: true
    },
    {
      id: 2,
      name: 'Alex Johnson',
      role: 'Engineering Manager at Meta',
      avatar: 'AJ',
      unread: 0,
      lastMessage: 'Let\'s schedule a meeting next week',
      timestamp: 'Yesterday',
      online: false,
      pinned: true
    },
    {
      id: 3,
      name: 'Tech Recruitment',
      role: 'Microsoft Recruiting Team',
      avatar: 'MS',
      unread: 12,
      lastMessage: 'Interview feedback available',
      timestamp: '2 days ago',
      online: true,
      pinned: false
    },
    {
      id: 4,
      name: 'Project Team',
      role: '5 members',
      avatar: 'PT',
      unread: 0,
      lastMessage: 'David uploaded new requirements',
      timestamp: '3 days ago',
      online: true,
      pinned: false
    },
    {
      id: 5,
      name: 'Emma Wilson',
      role: 'Data Scientist at Netflix',
      avatar: 'EW',
      unread: 0,
      lastMessage: 'The dataset looks perfect!',
      timestamp: '1 week ago',
      online: false,
      pinned: false
    },
    {
      id: 6,
      name: 'Michael Brown',
      role: 'Frontend Developer at Spotify',
      avatar: 'MB',
      unread: 0,
      lastMessage: 'Check out this new library',
      timestamp: '2 weeks ago',
      online: true,
      pinned: false
    }
  ];

  const messages = [
    {
      id: 1,
      sender: 'Sarah Chen',
      avatar: 'SC',
      content: 'Hi there! Thanks for connecting with me.',
      timestamp: 'Yesterday, 10:30 AM',
      isSent: false,
      read: true
    },
    {
      id: 2,
      sender: 'You',
      avatar: 'ME',
      content: 'Hello Sarah! Thanks for accepting my connection request.',
      timestamp: 'Yesterday, 10:35 AM',
      isSent: true,
      read: true
    },
    {
      id: 3,
      sender: 'Sarah Chen',
      avatar: 'SC',
      content: 'I really liked your recent project on GitHub. The architecture looks solid!',
      timestamp: 'Yesterday, 10:40 AM',
      isSent: false,
      read: true
    },
    {
      id: 4,
      sender: 'You',
      avatar: 'ME',
      content: 'Thank you! I noticed you work on some interesting design systems at Google.',
      timestamp: 'Yesterday, 10:45 AM',
      isSent: true,
      read: true
    },
    {
      id: 5,
      sender: 'Sarah Chen',
      avatar: 'SC',
      content: 'Yes, I\'d be happy to share some insights about our design process.',
      timestamp: 'Yesterday, 10:50 AM',
      isSent: false,
      read: true
    },
    {
      id: 6,
      sender: 'You',
      avatar: 'ME',
      content: 'That would be amazing! Would you be open to a quick chat next week?',
      timestamp: 'Yesterday, 11:00 AM',
      isSent: true,
      read: true
    },
    {
      id: 7,
      sender: 'Sarah Chen',
      avatar: 'SC',
      content: 'Absolutely! Here are the design files I mentioned earlier.',
      timestamp: 'Today, 10:25 AM',
      isSent: false,
      read: true
    },
    {
      id: 8,
      sender: 'Sarah Chen',
      avatar: 'SC',
      type: 'file',
      fileName: 'design-system-guide.pdf',
      fileSize: '2.4 MB',
      timestamp: 'Today, 10:27 AM',
      isSent: false,
      read: true
    },
    {
      id: 9,
      sender: 'Sarah Chen',
      avatar: 'SC',
      content: 'Thanks for sharing the design files! Looking forward to our chat.',
      timestamp: 'Today, 10:30 AM',
      isSent: false,
      read: true
    }
  ];

  const quickReplies = [
    'Thanks!',
    'Sounds good',
    'Let me check',
    'Available at 2 PM',
    'Can we reschedule?',
    'Perfect!',
    'Looking forward to it',
    'Thanks for sharing'
  ];

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim()) {
      console.log('Sending message:', message);
      setMessage('');
    }
  };

  const handleQuickReply = (reply) => {
    setMessage(reply);
  };

  const activeConversation = conversations.find(conv => conv.id === activeChat);

  return (
    <div className="messaging-page">
      {/* Header */}
      <div className="messages-header">
        <div className="messageheader-content">
          <h1 className="messagepage-title">Messaging</h1>
          <div className="header-actions">
            <button className="new-message-btn" onClick={() => setShowNewMessageModal(true)}>
              <MessageCircle size={20} />
              <span>New message</span>
            </button>
          </div>
        </div>
      </div>

      <div className="messages-container">
        {/* Left Sidebar - Conversations */}
        <div className="conversations-sidebar">
          {/* Search */}
          <div className="conversations-search">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search messages"
              className="messagesearch-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="filter-btn">
              <Filter size={20} />
            </button>
          </div>

          {/* Pinned Conversations */}
          <div className="conversations-section">
            <div className="messagesection-header">
              <Pin size={16} />
              <span>Pinned</span>
            </div>
            <div className="conversations-list">
              {conversations
                .filter(conv => conv.pinned)
                .map(conversation => (
                  <div
                    key={conversation.id}
                    className={`conversation-item ${activeChat === conversation.id ? 'active' : ''}`}
                    onClick={() => setActiveChat(conversation.id)}
                  >
                    <div className="conversation-avatar">
                      <div className={`avatar ${conversation.online ? 'online' : ''}`}>
                        {conversation.avatar}
                      </div>
                      {conversation.unread > 0 && (
                        <span className="unread-badge">{conversation.unread}</span>
                      )}
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-header">
                        <h4 className="conversation-name">{conversation.name}</h4>
                        <span className="conversation-time">{conversation.timestamp}</span>
                      </div>
                      <p className="conversation-role">{conversation.role}</p>
                      <p className="conversation-preview">
                        {conversation.lastMessage}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* All Conversations */}
          <div className="conversations-section">
            <div className="messagesection-header">
              <Users size={16} />
              <span>All Conversations</span>
            </div>
            <div className="conversations-list">
              {conversations
                .filter(conv => !conv.pinned)
                .map(conversation => (
                  <div
                    key={conversation.id}
                    className={`conversation-item ${activeChat === conversation.id ? 'active' : ''}`}
                    onClick={() => setActiveChat(conversation.id)}
                  >
                    <div className="conversation-avatar">
                      <div className={`avatar ${conversation.online ? 'online' : ''}`}>
                        {conversation.avatar}
                      </div>
                      {conversation.unread > 0 && (
                        <span className="unread-badge">{conversation.unread}</span>
                      )}
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-header">
                        <h4 className="conversation-name">{conversation.name}</h4>
                        <span className="conversation-time">{conversation.timestamp}</span>
                      </div>
                      <p className="conversation-role">{conversation.role}</p>
                      <p className="conversation-preview">
                        {conversation.lastMessage}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="chat-main">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-avatar">
                <div className={`avatar ${activeConversation?.online ? 'online' : ''}`}>
                  {activeConversation?.avatar}
                </div>
              </div>
              <div className="chat-info">
                <h3 className="chat-name">{activeConversation?.name}</h3>
                <p className="chat-status">
                  {activeConversation?.online ? 'Online' : 'Last seen recently'}
                </p>
              </div>
            </div>
            <div className="chat-header-actions">
              <button className="chat-action-btn">
                <Phone size={20} />
              </button>
              <button className="chat-action-btn">
                <VideoIcon size={20} />
              </button>
              <button className="chat-action-btn" onClick={() => setShowChatInfo(!showChatInfo)}>
                <Info size={20} />
              </button>
              <button className="chat-action-btn">
                <MoreVertical size={20} />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="messages-area">
            <div className="messages-list">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`message-wrapper ${msg.isSent ? 'sent' : 'received'}`}
                >
                  {!msg.isSent && (
                    <div className="message-avatar">
                      <div className="avatar">{msg.avatar}</div>
                    </div>
                  )}
                  <div className="message-content-wrapper">
                    {!msg.isSent && (
                      <div className="message-sender">{msg.sender}</div>
                    )}
                    <div className={`message-bubble ${msg.type || 'text'}`}>
                      {msg.type === 'file' ? (
                        <div className="file-message">
                          <FileText size={24} />
                          <div className="file-info">
                            <div className="file-name">{msg.fileName}</div>
                            <div className="file-size">{msg.fileSize}</div>
                          </div>
                          <button className="download-btn">
                            <FileUp size={16} />
                          </button>
                        </div>
                      ) : (
                        <p>{msg.content}</p>
                      )}
                      <div className="message-meta">
                        <span className="message-time">{msg.timestamp}</span>
                        {msg.isSent && (
                          <span className="message-status">
                            {msg.read ? <CheckCheck size={14} /> : <Check size={14} />}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Replies */}
            <div className="quick-replies">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  className="quick-reply-btn"
                  onClick={() => handleQuickReply(reply)}
                >
                  {reply}
                </button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <form className="message-input-area" onSubmit={handleSendMessage}>
            <div className="input-actions">
              <button
                type="button"
                className="input-action-btn"
                onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              >
                <Paperclip size={20} />
              </button>
              
              {showAttachmentMenu && (
                <div className="attachment-menu">
                  <button type="button" className="attachment-option">
                    <Image size={18} />
                    <span>Photo & Video</span>
                  </button>
                  <button type="button" className="attachment-option">
                    <FileText size={18} />
                    <span>Document</span>
                  </button>
                  <button type="button" className="attachment-option">
                    <Video size={18} />
                    <span>Share screen</span>
                  </button>
                  <button type="button" className="attachment-option">
                    <Calendar size={18} />
                    <span>Schedule event</span>
                  </button>
                  <button type="button" className="attachment-option">
                    <MapPin size={18} />
                    <span>Location</span>
                  </button>
                </div>
              )}

              <button type="button" className="input-action-btn">
                <Smile size={20} />
              </button>
            </div>
            
            <div className="message-input-wrapper">
              <input
                type="text"
                className="message-input"
                placeholder="Write a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(e)}
              />
              {message.trim() && (
                <button type="submit" className="send-btn">
                  <Send size={20} />
                </button>
              )}
              {!message.trim() && (
                <button type="button" className="voice-btn">
                  <Mic size={20} />
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Chat Info Sidebar */}
        {showChatInfo && (
          <div className="chat-info-sidebar">
            <div className="sidebar-header">
              <h3>Chat Info</h3>
              <button className="close-btn" onClick={() => setShowChatInfo(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="sidebar-messagesection">
              <div className="info-avatar">
                <div className="avatar large">
                  {activeConversation?.avatar}
                </div>
                <h4>{activeConversation?.name}</h4>
                <p>{activeConversation?.role}</p>
              </div>
            </div>

            <div className="sidebar-section">
              <h4 className="section-title">Shared Files</h4>
              <div className="shared-files">
                <div className="shared-file">
                  <FileText size={18} />
                  <div className="file-details">
                    <div className="file-name">design-system-guide.pdf</div>
                    <div className="file-meta">2.4 MB • Today</div>
                  </div>
                </div>
                <div className="shared-file">
                  <Image size={18} />
                  <div className="file-details">
                    <div className="file-name">wireframes.png</div>
                    <div className="file-meta">1.8 MB • Yesterday</div>
                  </div>
                </div>
              </div>
              <button className="view-all-btn">View all</button>
            </div>

            <div className="sidebar-section">
              <h4 className="section-title">Chat Settings</h4>
              <div className="settings-list">
                <div className="setting-item">
                  <div className="setting-info">
                    <Bell size={18} />
                    <span>Notifications</span>
                  </div>
                  <label className="switch">
                    <input type="checkbox" defaultChecked />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <Pin size={18} />
                    <span>Pin conversation</span>
                  </div>
                  <label className="switch">
                    <input type="checkbox" defaultChecked={activeConversation?.pinned} />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <Star size={18} />
                    <span>Mark as favorite</span>
                  </div>
                  <button className="star-btn">
                    <Star size={18} />
                  </button>
                </div>
              </div>
            </div>

            <div className="sidebar-section">
              <h4 className="section-title">Actions</h4>
              <div className="action-buttons">
                <button className="action-btn">
                  <Archive size={18} />
                  <span>Archive chat</span>
                </button>
                <button className="action-btn delete">
                  <Trash2 size={18} />
                  <span>Delete chat</span>
                </button>
                <button className="action-btn">
                  <BellOff size={18} />
                  <span>Mute notifications</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Message Modal */}
        {showNewMessageModal && (
          <div className="modal-overlay">
            <div className="new-message-modal">
              <div className="modal-header">
                <h3>New Message</h3>
                <button className="close-btn" onClick={() => setShowNewMessageModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-search">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search connections"
                  className="modal-search-input"
                />
              </div>
              <div className="modal-recent">
                <h4>Recent</h4>
                <div className="recent-contacts">
                  {conversations.slice(0, 4).map(contact => (
                    <div key={contact.id} className="recent-contact">
                      <div className="contact-avatar">
                        <div className="avatar">{contact.avatar}</div>
                      </div>
                      <div className="contact-info">
                        <div className="contact-name">{contact.name}</div>
                        <div className="contact-role">{contact.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowNewMessageModal(false)}>
                  Cancel
                </button>
                <button className="btn-primary">Start chat</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingPage;